import { computed, ref, type Ref } from "vue";
import * as yup from "yup";
import { FormModes } from "@/models/form";
import { StudentInformationModal, type StudentFormVM } from "@/models/student";
import {
  NationalIdNumberTypeIds,
  NationalIdNumberTypeDefinitions,
} from "@/models/nationalIdNumberTypes";
import type { FormSchema, FieldSchema, Option } from "@/models/form";
import useValidation from "@/use/validation";
import useFormHelpers from "@/use/formHelpers";
import type { VerifyApplicantNationalIdNumberRequest } from "@/models/api";
import { useStore } from "vuex";
import { $api } from "@/services/api";
import { useForm } from "vee-validate";
import useFormatter from "@/use/formatter";
import { CitizenshipStatusTypeIds, ConsentServiceEntityTypeIds } from "@/config/constants";

export interface ValidationData {
  studentInfo: Ref<any>;
  initialValues: Ref<StudentFormVM | undefined>;
  mode: Ref<keyof typeof FormModes>;
  entityTypeId: number;
}

export function useStudentInformationForm(data: ValidationData) {
  const store = useStore();

  const studentInformationModal = computed<StudentInformationModal>(
    () => store.state.applicationInfoModule.studentInformationModal,
  );

  const {
    getOption,
    getOptionLabel,
    getErrorMessage,
    isNationalIdNumberMasked,
    validItinRegex,
    validSSnRegex,
  } = useValidation();
  const { convertDateOfBirthToAge } = useFormHelpers();
  const { formatDateOfBirth } = useFormatter();

  const nationalIdNumberLabel = computed<string>(() => {
    return data.studentInfo.value?.nationalIdNumberTypeId == NationalIdNumberTypeIds.ITIN
      ? NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.ITIN]?.name
      : NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.SSN]?.name;
  });

  // #TODO
  const sameDOB = ref<boolean>(false);
  const sameSSN = ref<boolean>(false);

  const showAdditionalInfo = ref<boolean>(false);

  const citizenshipStatusOptions = computed<Option[]>(
    () =>
      store.state.applicationInfoModule?.studentInformationModal?.citizenshipStatusOptions || [],
  );

  const selectedCitizenshipStatusId = computed<number | undefined>(() => {
    const citizenshipStatusId = data.studentInfo.value?.citizenshipStatusId;
    if (!isNaN(Number(citizenshipStatusId))) {
      return Number(citizenshipStatusId);
    }
    return citizenshipStatusId;
  });

  const selectedCitizenshipStatus = computed<Option | undefined>(() => {
    if (selectedCitizenshipStatusId.value) {
      return citizenshipStatusOptions.value.find(
        (o: Option) => o.value === selectedCitizenshipStatusId.value,
      );
    }
    return undefined;
  });

  const showPermanentResidentReminder = computed<boolean>(() => {
    if (selectedCitizenshipStatusId.value === CitizenshipStatusTypeIds.PermanentResident) {
      return true;
    } else {
      return false;
    }
  });

  const schema = ref<FormSchema>({
    firstName: {
      name: "firstName",
      type: "text",
      label: "First Name",
      placeholder: "First Name",
      required: true,
      maxLength: 40,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value.firstName ? [errors.value.firstName] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    lastName: {
      name: "lastName",
      type: "text",
      label: "Last Name",
      placeholder: "Last Name",
      required: true,
      maxLength: 40,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value.lastName ? [errors.value.lastName] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    dateOfBirth: {
      name: "dateOfBirth",
      label: "Date of Birth",
      type: "date",
      placeholder: "YYYY-MM-DD",
      required: true,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value.dateOfBirth ? [errors.value.dateOfBirth] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    citizenshipStatusId: {
      name: "citizenshipStatusId",
      type: "select",
      label: "Citizenship Status",
      required: true,
      tooltipLocation: "bottom",
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value.citizenshipStatusId ? [errors.value.citizenshipStatusId] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    nationalIdNumber: {
      name: "nationalIdNumber",
      type: "nationalIdNumber",
      subtype: undefined,
      label: nationalIdNumberLabel.value,
      placeholder: "###-##-####",
      required: true,
      tooltipLocation: "bottom",
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value.nationalIdNumber ? [errors.value.nationalIdNumber] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
    itinIndicator: {
      name: "itinIndicator",
      type: "checkbox",
      label: "Indicates ITIN",
      required: false,
      tooltipLocation: "bottom",
    } as FieldSchema,
    parentConsent: {
      name: "parentConsent",
      type: "checkbox",
      label:
        "By checking this box, you affirm that you are the student's parent or legal guardian and that you claim the student as a dependent on your tax return.",
      errorMessage: "You must check the box to continue.",
      required: data.entityTypeId === ConsentServiceEntityTypeIds.Application,
      showRequiredIndicator: true,
      validate: (schema: FieldSchema) => {
        schema.errors = errors.value.parentConsent ? [errors.value.parentConsent] : [];
        schema.valid = schema.errors.length === 0;
        schema.touched = schema.errors.length > 0;
      },
    } as FieldSchema,
  });

  const formFields = computed(() => {
    const fields = [
      {
        name: schema.value.firstName.name,
        label: schema.value.firstName.label,
        type: schema.value.firstName.type,
        required: schema.value.firstName.required,
        value: data.studentInfo.value?.firstName,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        visible: true,
      },
      {
        name: schema.value.lastName.name,
        label: schema.value.lastName.label,
        type: schema.value.lastName.type,
        required: schema.value.lastName.required,
        value: data.studentInfo.value?.lastName,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        visible: true,
      },
      {
        name: schema.value.dateOfBirth.name,
        label: schema.value.dateOfBirth.label,
        type: schema.value.dateOfBirth.type,
        required: schema.value.dateOfBirth.required,
        value: data.studentInfo.value?.dateOfBirth,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        visible: true,
      },
      {
        name: schema.value.citizenshipStatusId.name,
        label: schema.value.citizenshipStatusId.label,
        type: schema.value.citizenshipStatusId.type,
        required: schema.value.citizenshipStatusId.required,
        value: data.studentInfo.value?.citizenshipStatusId,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        options: citizenshipStatusOptions.value,
        visible: showAdditionalInfo.value,
      },
      {
        name: schema.value.nationalIdNumber.name,
        label: schema.value.nationalIdNumber.label,
        type: schema.value.nationalIdNumber.type,
        required: schema.value.nationalIdNumber.required,
        value: data.studentInfo.value?.nationalIdNumber,
        width: "col-4",
        showOnAdd: true,
        showOnEdit: true,
        disabledOnEdit: false,
        options: citizenshipStatusOptions.value,
        visible: showAdditionalInfo.value,
      },
    ];

    return fields.filter(field => {
      if (data.mode.value === "add") {
        return field.showOnAdd !== false;
      } else if (data.mode.value === "edit") {
        return field.showOnEdit !== false;
      }

      return true;
    });
  });

  const nationalIdNumberValidationSchema = computed<yup.Schema>(() => {
    return yup
      .string()
      .transform((curr, orig) =>
        orig === "" || orig === undefined || orig === null ? undefined : curr,
      )
      .test("valid-itin", "Please provide a valid ITIN.", value => {
        if (data.studentInfo.value.nationalIdNumberTypeId === NationalIdNumberTypeIds.ITIN) {
          if (!value) {
            return false;
          }

          if (isNationalIdNumberMasked(value)) {
            return true;
          }
          const validITIN = validItinRegex.test(value);

          return validITIN;
        }
        return true;
      })
      .test("valid-ssn", "Please provide a valid SSN.", async (value: string | undefined) => {
        if (data.studentInfo.value.nationalIdNumberTypeId === NationalIdNumberTypeIds.SSN) {
          if (!value) {
            return false;
          }
          if (isNationalIdNumberMasked(value)) {
            return true;
          }
          const validSSN = validSSnRegex.test(value);

          return validSSN;
        }
        return true;
      })
      .test("is-unique", "Same as first applicant.", async value => {
        // only run verification when input exists
        if (value !== undefined) {
          if (isNationalIdNumberMasked(value)) {
            return true;
          }

          const cleanedValue = value.split("-").join("");
          if (cleanedValue.length === 9) {
            const nationalIdNumberCheckRequest = {
              nationalIdNumber: cleanedValue,
              applicantId: data.studentInfo.value.id || data.studentInfo.value.applicantId,
              applicationId: store.state.appData?.application?.id,
            } as VerifyApplicantNationalIdNumberRequest;

            const result = await $api.applicant.verifyApplicantNationalIdNumberRequest(
              nationalIdNumberCheckRequest,
            );

            if (result && result?.isVerified === false) {
              sameSSN.value = !result?.isVerified;

              if (!sameSSN.value) {
                sameDOB.value = false;
                await handleSingleFieldValidation("dateOfBirth");
              }
              return result?.isVerified;
            }
          }
        }

        return true;
      });
  });
  const validationSchema = computed<yup.Schema>(() => {
    return yup.object().shape({
      firstName: yup
        .string()
        .label(schema.value.firstName.label || "First Name")
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .required(schema.value.firstName.errorMessage)
        .test(
          "valid-input",
          `${schema.value.firstName.label} can only contain letters, apostrophes, hyphens, and spaces.`,
          value => {
            const validInput = /^[a-zA-Z\s'-]+$/.test(value);

            return validInput;
          },
        ),
      lastName: yup
        .string()
        .label(schema.value.lastName.label || "Last Name")
        .required(getErrorMessage(schema.value.lastName))
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .test(
          "valid-input",
          `${schema.value.lastName.label} can only contain letters, apostrophes, hyphens, and spaces.`,
          value => {
            const validInput = /^[a-zA-Z\s'-]+$/.test(value);

            return validInput;
          },
        ),
      citizenshipStatusId: yup
        .number()
        .label(schema.value.citizenshipStatusId?.label || "Citizenship Status")
        .transform(value => (isNaN(value) ? undefined : Number(value)))
        .required(getErrorMessage(schema.value.citizenshipStatusId))
        .min(1, getErrorMessage(schema.value.citizenshipStatusId)),
      nationalNumberTypeId: yup.number().notRequired(),
      nationalIdNumber: nationalIdNumberValidationSchema.value,
      dateOfBirth: yup
        .string()
        .label(schema.value.dateOfBirth.label || "Date of Birth")
        .transform((curr, orig) => (orig === "" ? undefined : curr))
        .required(`${schema.value.dateOfBirth.label} is required`)
        .test(
          "is-valid-ISO8601-format",
          `Please provide a valid ${schema.value.dateOfBirth.label}`,
          value => {
            const validISO8601Date = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(value);
            if (validISO8601Date) {
              const age = convertDateOfBirthToAge(value);
              if (age && age > 0 && age <= 150) {
                return true;
              }
            }

            return false;
          },
        ),
      parentConsent: yup
        .boolean()
        .test("is-valid", `${getErrorMessage(schema.value.parentConsent)}`, function (value) {
          if (!schema.value.parentConsent.required) {
            return true;
          }

          return value !== undefined && value !== null && value !== false;
        }),
    });
  });

  const { validate, errors, values, validateField, setFieldValue, setValues } = useForm({
    validationSchema: validationSchema.value,
    validateOnMount: false, // Disables validation on component mount
  });

  async function handleSingleFieldValidation(fieldName: string) {
    // Pass the name of the field you want to validate
    const { valid, errors } = await validateField(fieldName);

    return { valid, errors };
  }

  function handleFieldChange(fieldName: string, value: any) {
    setFieldValue(fieldName, value);
    switch (fieldName) {
      case "citizenshipStatusId":
        data.studentInfo.value.citizenshipStatusName =
          getOptionLabel(citizenshipStatusOptions.value, Number(value)) || "";
        break;
      case "nationalIdNumber":
        data.studentInfo.value.nationalIdNumber = value;
        break;
      case "itinIndicator":
        data.studentInfo.value.itinIndicator = value;
        data.studentInfo.value.nationalIdNumberTypeId =
          value === true ? NationalIdNumberTypeIds.ITIN : NationalIdNumberTypeIds.SSN;
        break;
      default:
        return;
    }
  }

  return {
    citizenshipStatusOptions,
    errors,
    formatDateOfBirth,
    formFields,
    getOption,
    getOptionLabel,
    handleFieldChange,
    isNationalIdNumberMasked,
    nationalIdNumberLabel,
    nationalIdNumberValidationSchema,
    schema,
		selectedCitizenshipStatus,
		selectedCitizenshipStatusId,
		showPermanentResidentReminder,
    setValues,
    setFieldValue,
    studentInformationModal,
    validate,
    validationSchema,
    values,
  };
}
